import type {
  LargeRechargeLevel,
  LargeRechargeList,
  LargeRechargeNotice,
  LargeRechargePage,
  LargeRechargeQuery,
  LargeRechargeRule,
  SaveLargeRechargeRequest,
  SaveLargeRechargeRule,
} from './type/large-recharge-monitor'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'

const rulePaths = { global: 'Global', customer: 'Customer', account: 'Account' }

export const largeRechargeMonitorApi = defineApi('operation', ({ prefix }) => {
  /**
   * 查询大盘、客户或账户规则分页。
   * POST /adsLargeRecharge/queryGlobalPage、queryCustomerPage、queryAccountPage
   */
  function queryRules(
    level: LargeRechargeLevel,
    params: LargeRechargeQuery,
  ): Promise<LargeRechargeList<LargeRechargeRule>> {
    const condition = params.condition
    const request: LargeRechargeQuery = {
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      sortBy: params.sortBy,
      orderBy: params.orderBy,
      condition:
        level === 'global'
          ? {
              currency: condition.currency,
              notifyEnabled: condition.notifyEnabled,
              limitEnabled: condition.limitEnabled,
            }
          : {
              customerId: condition.customerId
                ? Number(condition.customerId)
                : undefined,
              customerName: condition.customerName,
              ...(level === 'customer'
                ? { currency: condition.currency }
                : {
                    mediaPlatform: condition.mediaPlatform,
                    accountId: condition.accountId,
                    accountName: condition.accountName,
                  }),
            },
    }
    return requestClient
      .post<
        LargeRechargePage<LargeRechargeRule>
      >(prefix(`adsLargeRecharge/query${rulePaths[level]}Page`), request)
      .then((res) => ({ items: res?.list ?? [], total: res?.total }))
  }

  /**
   * 新增或编辑规则；编辑传 id，账户规则不传客户和币种。
   * POST /adsLargeRecharge/saveGlobal、saveCustomer、saveAccount
   */
  function saveRule(params: SaveLargeRechargeRule) {
    const request: SaveLargeRechargeRequest = {
      id: params.id,
      thresholdAmount: String(params.thresholdAmount),
      notifyEnabled: params.notifyEnabled,
      limitEnabled: params.limitEnabled,
      ...(params.level === 'account'
        ? { mediaPlatform: params.mediaPlatform, accountId: params.accountId }
        : {
            currency: params.currency,
            ...(params.level === 'customer'
              ? { customerId: Number(params.customerId) }
              : {}),
          }),
    }
    return requestClient.post<undefined>(
      prefix(`adsLargeRecharge/save${rulePaths[params.level]}`),
      request,
    )
  }

  /**
   * 按规则 ID 软删除指定层级的规则。
   * POST /adsLargeRecharge/deleteGlobal、deleteCustomer、deleteAccount
   */
  function deleteRule(level: LargeRechargeLevel, id: number) {
    return requestClient.post<undefined>(
      prefix(`adsLargeRecharge/delete${rulePaths[level]}`),
      { id },
    )
  }

  /**
   * 查询飞书通知记录；notifyTime 直接传东八区 YYYY-MM-DD 日期闭区间。
   * POST /adsLargeRecharge/queryNotifyPage
   */
  function queryNotices(
    params: LargeRechargeQuery,
  ): Promise<LargeRechargeList<LargeRechargeNotice>> {
    const condition = params.condition
    const request: LargeRechargeQuery = {
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      sortBy: params.sortBy,
      orderBy: params.orderBy,
      condition: {
        notifyTime: condition.notifyTime,
        customerId: condition.customerId
          ? Number(condition.customerId)
          : undefined,
        customerName: condition.customerName,
        accountId: condition.accountId,
        mediaPlatform: condition.mediaPlatform,
        hitScope: condition.hitScope,
        rechargeSource: condition.rechargeSource,
        verified: condition.verified,
        sendStatus: condition.sendStatus,
      },
    }
    return requestClient
      .post<
        LargeRechargePage<LargeRechargeNotice>
      >(prefix('adsLargeRecharge/queryNotifyPage'), request)
      .then((res) => ({ items: res?.list ?? [], total: res?.total }))
  }

  return {
    queryRules,
    saveRule,
    deleteRule,
    queryNotices,
  }
})
